import { useCallback, useMemo } from 'react';

import classNames from 'classnames';

import { ButtonIcon, IconArrowDown } from '@spotify-internal/encore-web';
import { AiLoadingIndicator } from '@spotify/confidence-core-react';
import { ConversationsV2ChatState } from '@spotify/confidence-graphql';

import { useChatEventExtensions, useToolExtensions } from '../../extensions';
import {
  findLastThoughtProcessIdx,
  groupChatEvents,
  groupThoughtProcess,
  useCachedChatEvents,
  useStickToBottom,
} from '../../hooks';
import { buildInlineBlocksIndex } from '../AssistantMessage/inlineBlocks';
import { ThoughtProcess } from '../ThoughtProcess';
import { ToolCallGroup } from '../ToolCallGroup';
import { ChatContextProvider } from './ChatContext';
import { SubagentPanelProvider } from './SubagentPanelContext';
import { ChatThreadTailProps } from './types';

import styles from './ChatThread.module.css';

export const ChatThreadTail = ({
  chatName,
  chatState,
  limit = 10,
  minCreateTime,
  disablePadding = false,
}: ChatThreadTailProps) => {
  const { scrollRef, contentRef, isAtBottom, scrollToBottom } =
    useStickToBottom();
  const isRunning = chatState === ConversationsV2ChatState.Running;
  const { chatEvents } = useCachedChatEvents(chatName);
  const tailEvents = useMemo(() => {
    const filteredEvents =
      minCreateTime === undefined
        ? (chatEvents ?? [])
        : (chatEvents ?? []).filter(event => event.createTime >= minCreateTime);

    return limit > 0 ? filteredEvents.slice(-limit) : filteredEvents;
  }, [chatEvents, limit, minCreateTime]);
  const toolExtensions = useToolExtensions();
  const chatEventExtensions = useChatEventExtensions();
  const displayItems = useMemo(
    () => groupChatEvents(tailEvents, toolExtensions),
    [tailEvents, toolExtensions],
  );
  const processedItems = useMemo(
    () => groupThoughtProcess(displayItems, isRunning, toolExtensions),
    [displayItems, isRunning, toolExtensions],
  );
  const lastThoughtProcessIdx = useMemo(
    () => findLastThoughtProcessIdx(processedItems),
    [processedItems],
  );
  const showStandaloneLoading =
    isRunning &&
    processedItems[processedItems.length - 1]?.type !== 'thought-process';
  const chatContextValue = useMemo(
    // Blocks are indexed over the full history: a tail message may reference a
    // block tool call that falls outside the `limit` window.
    () => ({
      chatName,
      chatEvents: tailEvents,
      inlineBlocks: buildInlineBlocksIndex(chatEvents ?? []),
    }),
    [chatName, tailEvents, chatEvents],
  );
  const handleOpenSubagentPanel = useCallback(() => {}, []);

  return (
    <ChatContextProvider value={chatContextValue}>
      <SubagentPanelProvider openSubagentPanel={handleOpenSubagentPanel}>
        <div className={styles.root}>
          <div className={styles.container}>
            <div className={styles.messagesWrapper}>
              <div
                className={classNames(styles.messages, {
                  [styles.messagesWithoutPadding]: disablePadding,
                })}
                data-testid="chat-thread-tail-messages"
                ref={scrollRef}
              >
                <div className={styles.messagesInner} ref={contentRef}>
                  <div className={styles.messagesContent}>
                    {processedItems.map((item, idx) => {
                      if (item.type === 'thought-process') {
                        const isLatestThoughtProcess =
                          idx === lastThoughtProcessIdx;

                        return (
                          <ThoughtProcess
                            key={item.key}
                            items={item.items}
                            isActive={isRunning && isLatestThoughtProcess}
                            isInterrupted={false}
                          />
                        );
                      }

                      if (item.type === 'tool-group') {
                        return (
                          <ToolCallGroup
                            key={item.key}
                            entries={item.entries}
                          />
                        );
                      }

                      const extension = chatEventExtensions.find(ext =>
                        ext.predicate(item.event),
                      );

                      if (!extension) {
                        return null;
                      }

                      return (
                        <div key={item.key}>{extension.render(item.event)}</div>
                      );
                    })}
                    {showStandaloneLoading && (
                      <div className={styles.loadingIndicator}>
                        <AiLoadingIndicator />
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div
                className={classNames(styles.scrollToBottomButton, {
                  [styles.scrollToBottomButtonVisible]: !isAtBottom,
                })}
                aria-hidden={isAtBottom}
              >
                <ButtonIcon
                  variant="tertiary"
                  onClick={scrollToBottom}
                  aria-label="Scroll to bottom"
                  tabIndex={isAtBottom ? -1 : undefined}
                 data-track-source-anchor="501af53a80;;low">
                  <IconArrowDown />
                </ButtonIcon>
              </div>
            </div>
          </div>
        </div>
      </SubagentPanelProvider>
    </ChatContextProvider>
  );
};
